import { useMemo, useRef, useState } from 'react';

import { LegalClientInnerItemEntity } from 'api/types/entity';
import { formatToDate } from 'helpers';
import { useFormContext } from 'libs/form/useFormContext';
import { useTranslation } from 'libs/i18n';
import { IconButton } from 'libs/ui';
import { useDialog } from 'libs/ui/Dialog/useDialog';
import { Icon } from 'libs/ui/Icon';
import {
  DialogDeleteAndReasonChange,
  Section,
} from 'modules/client/common/components';

import { FirstHeadArchiveDialog } from './FirstHeadArchiveDialog';
import { FirstHeadItem, FirstHeadsInfoCard } from './FirstHeadsInfoCard';

export interface CompanyFirstHeadsSectionProps {
  firstHeads: LegalClientInnerItemEntity[];
  onClickFirstHead?: (id: string) => void;
  onClickAddFirstHead?: () => void;
  onDeleteFirstHead?: (id: string, reason: string) => Promise<void>;
  onArchiveFirstHead?: (id: string, reason: string) => Promise<void>;
  onDearchiveFirstHead?: (id: string, reason: string) => Promise<void>;
}

export const CompanyFirstHeadsSection = ({
  firstHeads,
  onClickFirstHead,
  onClickAddFirstHead,
  onDeleteFirstHead,
  onArchiveFirstHead,
  onDearchiveFirstHead,
}: CompanyFirstHeadsSectionProps) => {
  const { t } = useTranslation();

  const { readOnly } = useFormContext();

  const activeHeadItem = useRef<FirstHeadItem>();

  const [dialogLoading, setDialogLoading] = useState(false);

  const {
    isOpen: isOpenDeleteDialog,
    onClose: onCloseDeleteDialog,
    open: openDialogReason,
  } = useDialog();
  const { open: openArchiveDialog, ...archiveDialogProps } = useDialog();

  const items: FirstHeadItem[] = useMemo(() => {
    const result: FirstHeadItem[] = firstHeads.map((h) => ({
      id: h.id,
      name: h.name,
      createdAt: h.createdAt,
      archived: h.archived,
      position: h.positionInCompany?.position || '',
      expirationDate: h.positionInCompany?.expirationDate
        ? formatToDate(h.positionInCompany.expirationDate)
        : undefined,
      residentialCountry: h.residentialCountry,
      registrationCountry: h.registrationCountry,
    }));

    result.sort((a, b) =>
      a.archived === b.archived
        ? b.createdAt.getTime() - a.createdAt.getTime()
        : a.archived
        ? 1
        : -1
    );

    return result;
  }, [firstHeads]);

  const onClickDelete = onDeleteFirstHead
    ? (row: FirstHeadItem) => {
        activeHeadItem.current = row;
        openDialogReason();
      }
    : undefined;

  const onClickArchive = onArchiveFirstHead
    ? (row: FirstHeadItem) => {
        activeHeadItem.current = row;
        openArchiveDialog();
      }
    : undefined;

  const onDelete = async (reason: string) => {
    setDialogLoading(true);
    if (activeHeadItem.current && onDeleteFirstHead) {
      try {
        await onDeleteFirstHead(activeHeadItem.current.id, reason);
      } catch (error) {
        /* empty */
      }
    }
    setDialogLoading(false);
    onCloseDeleteDialog();
  };

  return (
    <Section
      datax="companyFirstHeadsSection"
      headerRight={
        !readOnly &&
        onClickAddFirstHead && (
          <IconButton
            icon={<Icon.Plus size={16} />}
            mode="outlined"
            sizeVariant="xs"
            onClick={onClickAddFirstHead}
          />
        )
      }
      title={t('client.legal.legalClientLegalHead.firstHeads.title')}
    >
      <FirstHeadsInfoCard
        editable={!readOnly}
        items={items}
        onClickArchive={onClickArchive}
        onClickDelete={onClickDelete}
        onClickFirstHead={(item) => onClickFirstHead?.(item.id)}
      />

      <DialogDeleteAndReasonChange
        deleteLoading={dialogLoading}
        isOpen={isOpenDeleteDialog}
        onClose={onCloseDeleteDialog}
        onDelete={onDelete}
      />

      <FirstHeadArchiveDialog
        activeHeadItem={activeHeadItem.current}
        onArchive={onArchiveFirstHead}
        onDearchive={onDearchiveFirstHead}
        {...archiveDialogProps}
      />
    </Section>
  );
};
